
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, forwardRef, inject } from '@angular/core';
import { IAnnotation, IAnnotationAttachment } from 'src/app/model/thingie/annotation';
import { AnnotationAttachmentListComponent } from '../annotation-attachment-list/annotation-attachment-list.component';
import { AnnotationViewModule } from '../annotation-view.module';

import { IAttachmentLocation, ThingieService } from 'src/app/services/thingie/thingie-service.service';
import { hashFile } from 'src/app/utility/file-hash';
import { DefaultConfig } from 'src/assets/default.config';
import { FileDragDropComponent, ISelectedFileInfo } from '../../input-controls/file-drag-drop/file-drag-drop.component';
import { IDownloadableAttachment } from '../model/downloadable-attachment';
import { ITemporaryAttachment, isTemporaryAttachment } from '../model/temporary-attachment';

type AnnotationLocation = {
  thingieId: string;
  processIndex: number;
  annotationIndex: number;
};

/**
 * Component which helps with managing annotation attachments
 *
 * Wraps {@link AnnotationAttachmentListComponent} to dispay a list of annotations
 * and {@link AnnotationFileDragDropComponent} to add new annotations.
 */
@Component({
  selector: 'app-annotation-attachment-manager[annotation]',
  templateUrl: './annotation-attachment-manager.component.html',
  styleUrls: ['./annotation-attachment-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AnnotationAttachmentListComponent,
    forwardRef(() => AnnotationViewModule),
    FileDragDropComponent
  ]
})
export class AnnotationAttachmentManagerComponent implements OnDestroy {

  /**
   * The annotation to manage
   */
  @Input({ required: true })
  public set annotation(val: IAnnotation) {
    this.downloadableAttachments = [];

    if (this.addingNewAttachments) {
      throw new Error('Cannot change annotation while adding new attachments. Possible race condition?');
    }

    this.addingNewAttachments = true;
    void this.addToDownloadableAttachments(val.files)
      .finally(() => this.addingNewAttachments = false);
  }


  /**
   * Whether to show the actions column
   */
  @Input() public mode: 'view' | 'edit' = 'view';


  /**
   * Whether new attachments can be added
   */
  @Input() public canAddAttachments = false;


  /**
   * Event emitted when the annotation was updated (attachments added/removed)
   */
  @Output() public annotationAttachmentUpdated = new EventEmitter<void>();

  /**
   * Attachments displayed by the attachment list
   *
   * @private
   * @see {@link AnnotationAttachmentListComponent}
   */
  protected downloadableAttachments: IDownloadableAttachment[] = [];


  /**
   * Repository for object URLs of temporary attachments
   *
   * Key: sha256 hash of file
   * Value: Object URL
   *
   * IMPORTANT: Object URLs must be revoked when no longer needed
   */
  private objectUrls = new Map<string, string>();


  /**
   * Whether new attachments are currently being added
   *
   * Guards against race condition (see {@link annotation})
   */
  private addingNewAttachments = false;


  /**
   * Attachments that are to be deleted
   */
  private markedForDeletion: {
    attachment: IAnnotationAttachment;

    /**
     * Index of the attachment in the annotation
     */
    index: number;
  }[] = [];

  /**
   * Attachments that are to be shown again
   */
  private markedForUndelete: {
    attachment: IAnnotationAttachment;

    /**
     * Index of the attachment in the annotation
     */
    index: number;
  }[] = [];


  private changeDetectorRef = inject(ChangeDetectorRef);


  private thingieService = inject(ThingieService);


  /**
   * @private
   */
  public ngOnDestroy(): void {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
  }

  /**
   * Commit attachment changes to the provided annotation
   *
   * - Removes attachments marked for deletion
   * - Adds new attachments
   *
   * @param location The location of the annotation to commit changes to
   */
  public async commitChanges(param: AnnotationLocation | AnnotationLocation[]) {
    // Remove marked attachments & undo deletions for re-additions
    //
    // Currently only working if there is only one
    // annotation, as otherwise annotation locations and attachment
    // locations would have to be provided, which is not necessary right now.
    if (!Array.isArray(param)) {
      const deletionDto: IAttachmentLocation[] = [];

      for (const attachment of this.markedForDeletion) {
        deletionDto.push({
          thingieId: param.thingieId,
          processIdx: param.processIndex,
          annotationIdx: param.annotationIndex,
          attachmentIdx: attachment.index
        });
      }

      let removal$ = Promise.resolve();
      if (deletionDto.length > 0) {
        removal$ = this.thingieService.removeAttachments(deletionDto);
      }

      // Undelete attachments that were previously "deleted"
      if (this.markedForUndelete.length > 0) {
        const undeletionDto: IAttachmentLocation[] = [];
        for (const attachment of this.markedForUndelete) {
          undeletionDto.push({
            thingieId: param.thingieId,
            processIdx: param.processIndex,
            annotationIdx: param.annotationIndex,
            attachmentIdx: attachment.index
          });
        }

        const undelete$ = this.thingieService.undeleteAttachments(undeletionDto);

        await Promise.all([removal$, undelete$]);
      }
    }

    // Add new attachments

    const annotationLocations = Array.isArray(param) ? param : [param];

    const newAttachments: File[] = this.downloadableAttachments
      .filter((it): it is ITemporaryAttachment => isTemporaryAttachment(it))
      .map(it => it.file);

    if (newAttachments.length > 0) {
      // Note: this inefficiently uploads the attachment to every thingie separately. Can be improved.
      // see https://aquila-biolabs.atlassian.net/browse/COSOFT-2589
      for (const location of annotationLocations) {
        await this.thingieService.uploadAttachment(
          location.thingieId,
          location.annotationIndex,
          newAttachments
        );
      }
    }
  }


  /**
   * @private
   * @param files
   */
  protected async _addNewAttachments(files: ISelectedFileInfo[]) {
    await this.addToDownloadableAttachments(files
      .filter(f => !f.errors.length)
      .map(f => f.file));
  }


  /**
   * @private
   * @param attachment
   */
  protected _removeAttachment(attachment: IAnnotationAttachment) {
    const idx = this.downloadableAttachments.findIndex(
      it => it.sha256Hash === attachment.sha256Hash
    );

    if (isTemporaryAttachment(attachment)) {
      // revoke object URL
      URL.revokeObjectURL(attachment.downloadUri);
      this.objectUrls.delete(attachment.sha256Hash);
    } else {
      // Create deletion-action
      this.markedForDeletion.push({
        attachment,
        index: idx
      });
    }

    // Remove from list of downloadable attachments
    if (idx !== -1) {
      this.downloadableAttachments.splice(idx, 1);
    }

    this.downloadableAttachments = [...this.downloadableAttachments];

    this.changeDetectorRef.markForCheck();
    this.annotationAttachmentUpdated.emit();
  }


  /**
   * Adds the given attachments & files to the downloadable attachments
   *
   * Will turn attachments and files into {@link IDownloadableAttachment}s.
   * Triggers change detection.
   *
   * @param entries Attachments and files to add
   */
  private async addToDownloadableAttachments(entries: (IAnnotationAttachment | File)[]) {
    const attachments: IDownloadableAttachment[] = [];

    for (const entry of entries) {
      if (entry instanceof File) {
        const downloadableAttachment = await this.convertFileToDownloadableAttachment(entry);
        attachments.push(downloadableAttachment);
      } else {
        const downloadableAttachment = this.convertAttachmentToDownloadableAttachment(entry);
        attachments.push(downloadableAttachment);
      }
    }

    // To the downloadable attachments add those that are not in the list already
    attachments.forEach(att => {
      const availableAttIdx = this.downloadableAttachments.findIndex(it => it.sha256Hash === att.sha256Hash);
      if (availableAttIdx !== -1) {
        const availableAtt = this.downloadableAttachments[availableAttIdx];
        // Check if, the attachment has it's `deleted` flag set
        if (availableAtt.deleted) {
          // Set deleted to false, to show it in the list again
          availableAtt.deleted = false;

          // Mark it for undeletion, to persist this change
          this.markedForUndelete.push({
            attachment: availableAtt,
            index: availableAttIdx
          });
        }

        // Early return from `.forEach(...)`
        return;
      }

      // If the attachment was marked for deletion, re-introduce it
      const markedForDeletion = this.markedForDeletion.find(it => it.attachment.sha256Hash === att.sha256Hash);
      if (markedForDeletion) {
        // Remove it from "marked for deletion"
        this.markedForDeletion.splice(this.markedForDeletion.indexOf(markedForDeletion), 1);

        // re-insert into downloadable attachments
        this.downloadableAttachments.splice(
          markedForDeletion.index,
          0,
          markedForDeletion.attachment as IDownloadableAttachment
        );
      } else {
        // It's new, just add it to the end of the list
        this.downloadableAttachments.push(att);
      }
    });

    this.downloadableAttachments = [...this.downloadableAttachments];

    this.changeDetectorRef.markForCheck();

    if (!this.addingNewAttachments) {
      this.annotationAttachmentUpdated.emit();
    }
  }


  private convertAttachmentToDownloadableAttachment(attachment: IAnnotationAttachment): IDownloadableAttachment {
    return {
      ...attachment,
      downloadUri: DefaultConfig.uris.thingieAttachment
        .replace('{hash}', attachment.sha256Hash)
    };
  }


  private async convertFileToDownloadableAttachment(file: File): Promise<ITemporaryAttachment> {
    const hash = await hashFile(file, 'SHA-256');

    let objectUrl: string;
    if (this.objectUrls.has(hash)) {
      objectUrl = this.objectUrls.get(hash)!;
    } else {
      objectUrl = URL.createObjectURL(file);
      this.objectUrls.set(hash, objectUrl);
    }

    return {
      file,
      deleted: false,
      downloadUri: objectUrl,
      fileSize: file.size,
      mimetype: file.type,
      originalFileName: file.name,
      sha256Hash: hash,
      timestamp: ''
    };
  }
}
